/**
 * My Progress page — Phase A gamification surface.
 *
 * Three sections:
 *
 * - **Badge shelf** — every active badge, ordered by sort_order. Earned
 *   badges render in color with the awarded date; locked badges render
 *   greyscale with the criterion line (so the learner always sees how to
 *   earn the next one). No badge hidden (GAM-US-2).
 * - **Streak card** — current/longest streak + freeze tokens. Phase A
 *   always renders the zero object (no streak writes until Phase B);
 *   the card layout is finalized so Phase B is a data-only change.
 * - **XP card** — total + by-source breakdown.
 *
 * Mirrors ``MyJournalPage.tsx`` — hand-rolled ``LoadState`` discriminated
 * union, ``useEffect`` + ``cancelled`` flag, ``fetchFn`` test seam, and
 * the same ``onUnauthenticated`` redirect on 401.
 *
 * Marks the whole shelf seen on mount (so revisiting clears any toast
 * the user might've missed). The toast itself lives in BadgeToast.tsx
 * and runs at the App layout level — this page just ensures shelf
 * visits also flip seen_at.
 */
import type * as React from "react";
import { useEffect, useMemo, useState } from "react";

import { API_BASE_URL, ApiError } from "@/lib/api";
import {
  type BadgeStatus,
  type BadgesResponse,
  type StreakResponse,
  type XpResponse,
  getBadges,
  getStreak,
  getXp,
  markBadgesSeen,
} from "@/lib/gamification-api";

type LoadState =
  | { kind: "loading" }
  | {
      kind: "ready";
      badges: BadgesResponse;
      streak: StreakResponse;
      xp: XpResponse;
    }
  | { kind: "error"; message: string };

interface ProgressFetchers {
  badges: typeof getBadges;
  streak: typeof getStreak;
  xp: typeof getXp;
  markSeen: typeof markBadgesSeen;
}

const DEFAULT_FETCHERS: ProgressFetchers = {
  badges: getBadges,
  streak: getStreak,
  xp: getXp,
  markSeen: markBadgesSeen,
};

function redirectToLogin(returnTo: string): void {
  const encoded = encodeURIComponent(returnTo);
  window.location.href = `${API_BASE_URL}/api/auth/login?return_to=${encoded}`;
}

export interface ProgressPageProps {
  /** Test seam. */
  onUnauthenticated?: (returnTo: string) => void;
  fetchers?: Partial<ProgressFetchers>;
}

const SOURCE_LABEL: Record<string, string> = {
  lesson: "Lessons",
  module_quiz: "Module quizzes",
  exam: "Final exam",
  capstone: "Capstone",
  streak: "Streak milestones",
};

/**
 * Per-tier badge medallion treatment. Bronze/silver/gold each map to a
 * coherent ring+surface pair; gold gets the brand gradient because
 * gold = the milestone reward surface in our identity language.
 */
const TIER_STYLE: Record<
  string,
  { ring: string; surface: string; medal: string }
> = {
  bronze: {
    ring: "ring-amber-700/30",
    surface: "bg-amber-50",
    medal: "bg-gradient-to-br from-amber-600 to-amber-800 text-white",
  },
  silver: {
    ring: "ring-slate-400/40",
    surface: "bg-slate-50",
    medal: "bg-gradient-to-br from-slate-400 to-slate-600 text-white",
  },
  gold: {
    ring: "ring-ca-pink/30",
    surface: "bg-ca-gradient-subtle",
    medal: "bg-ca-gradient text-white",
  },
};

export function ProgressPage({
  onUnauthenticated = redirectToLogin,
  fetchers: fetchersOverride = {},
}: ProgressPageProps = {}): React.JSX.Element {
  const fetchers = useMemo<ProgressFetchers>(
    () => ({ ...DEFAULT_FETCHERS, ...fetchersOverride }),
    [fetchersOverride],
  );
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [badges, streak, xp] = await Promise.all([
          fetchers.badges(),
          fetchers.streak(),
          fetchers.xp(),
        ]);
        if (cancelled) return;
        setState({ kind: "ready", badges, streak, xp });
        // Shelf visit flips seen_at on every still-unseen badge — the
        // toast (BadgeToast.tsx, mounted in App) won't re-show them.
        // Best-effort: failure here is silent and the toast guards
        // against duplicate display via the server-side seen_at check.
        const unseenSlugs = badges.badges
          .filter((b) => b.earned && !b.seen)
          .map((b) => b.slug);
        if (unseenSlugs.length > 0) {
          void fetchers.markSeen(unseenSlugs).catch(() => {
            /* tolerated: the toast still uses server seen_at */
          });
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          onUnauthenticated("/progress");
          return;
        }
        const msg =
          err instanceof Error ? err.message : "Could not load your progress.";
        setState({ kind: "error", message: msg });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [onUnauthenticated, fetchers]);

  if (state.kind === "loading") {
    return (
      <section
        className="mx-auto max-w-4xl px-4 py-12 sm:px-6"
        aria-busy="true"
      >
        <p className="text-sm text-muted-foreground" role="status">
          Loading your progress…
        </p>
      </section>
    );
  }

  if (state.kind === "error") {
    return (
      <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
        <h1 className="text-3xl font-bold text-foreground">My Progress</h1>
        <p className="mt-4 text-sm text-red-700" role="alert">
          {state.message}
        </p>
      </section>
    );
  }

  const { badges, streak, xp } = state;
  return (
    <section className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
      {/* Hero header with brand gradient accent rail. The page's first
       *  job is to make the learner feel their accumulated effort, so
       *  the headline numbers get oversize typography and a milestone-
       *  tier surface treatment rather than the tabular look. */}
      <header className="relative overflow-hidden rounded-2xl border border-border bg-ca-gradient-subtle p-6 sm:p-8">
        <div className="pointer-events-none absolute inset-y-0 left-0 w-1 bg-ca-gradient" aria-hidden="true" />
        <p className="text-xs font-semibold uppercase tracking-wider text-ca-indigo">
          My Progress
        </p>
        <h1 className="mt-1 font-display text-4xl font-bold text-foreground sm:text-5xl">
          Look how far you've come.
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {badges.earned_count} of {badges.total_count} badges earned ·{" "}
          <span className="font-semibold text-foreground">{xp.total_xp.toLocaleString()}</span> XP ·{" "}
          {streak.current_len} day streak
        </p>
      </header>

      <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
        <StreakCard streak={streak} />
        <XpCard xp={xp} />
      </div>

      <section className="mt-12">
        <h2 className="font-display text-2xl font-semibold text-foreground">
          Badge shelf
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Earned badges show the date you earned them. Locked badges show the
          criterion — keep going.
        </p>
        <ul
          className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
          role="list"
        >
          {badges.badges.map((b) => (
            <BadgeShelfItem key={b.slug} badge={b} />
          ))}
        </ul>
      </section>
    </section>
  );
}

function StreakCard({ streak }: { streak: StreakResponse }): React.JSX.Element {
  const lit = streak.current_len > 0;
  return (
    <div className="relative overflow-hidden rounded-xl border border-border bg-card p-6 shadow-soft">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Daily streak
          </h2>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-display text-5xl font-bold text-foreground">
              {streak.current_len}
            </span>
            <span className="text-sm text-muted-foreground">
              day{streak.current_len === 1 ? "" : "s"} in a row
            </span>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Longest: {streak.longest_len} day
            {streak.longest_len === 1 ? "" : "s"} · Freeze tokens:{" "}
            {streak.freeze_tokens}
          </p>
        </div>
        {/* Flame medallion — lit (pink→orange gradient) when current_len
         *  > 0, otherwise rendered as a faded muted state so the visual
         *  weight matches the user's reality. */}
        <div
          className={[
            "flex h-16 w-16 shrink-0 items-center justify-center rounded-full",
            lit ? "bg-ca-gradient-subtle ring-1 ring-ca-pink/30" : "bg-muted",
          ].join(" ")}
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" className="h-8 w-8">
            <defs>
              <linearGradient id="streak-card-flame" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="hsl(330 81% 60%)" />
                <stop offset="100%" stopColor="hsl(25 95% 61%)" />
              </linearGradient>
            </defs>
            <path
              fill={lit ? "url(#streak-card-flame)" : "hsl(var(--muted-foreground) / 0.4)"}
              d="M12 2c1 3.5 4 5 4 8a4 4 0 0 1-8 0c0-2 1.5-3 1.5-5 0 0 .5 1 1 1 0 0-.5-2 1.5-4Zm-4.5 12a4.5 4.5 0 1 0 9 0c0-2-1-3-1.5-4-.5 1.5-1.5 2-3 2-2 0-2.5-2-2.5-2-.5 1.5-2 2-2 4Z"
            />
          </svg>
        </div>
      </div>
    </div>
  );
}

function XpCard({ xp }: { xp: XpResponse }): React.JSX.Element {
  const orderedSources = ["lesson", "module_quiz", "exam", "capstone", "streak"];
  return (
    <div className="rounded-xl border border-border bg-card p-6 shadow-soft">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Total XP
      </h2>
      <div className="mt-2 font-display text-5xl font-bold text-ca-gradient">
        {xp.total_xp.toLocaleString()}
      </div>
      <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        {orderedSources.map((src) => (
          <div key={src} className="contents">
            <dt className="text-muted-foreground">{SOURCE_LABEL[src] ?? src}</dt>
            <dd className="text-right font-semibold text-foreground">
              {(xp.by_source[src] ?? 0).toLocaleString()}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function BadgeShelfItem({ badge }: { badge: BadgeStatus }): React.JSX.Element {
  const tier = TIER_STYLE[badge.tier] ?? {
    ring: "ring-border",
    surface: "bg-card",
    medal: "bg-muted text-muted-foreground",
  };
  const awardedLine =
    badge.earned && badge.awarded_at
      ? `Earned ${new Date(badge.awarded_at).toLocaleDateString()}`
      : "Locked";
  return (
    <li
      className={[
        "rounded-xl border border-border p-4 shadow-soft transition-all",
        badge.earned
          ? `${tier.surface} hover:shadow-card`
          : "bg-card opacity-70",
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        <div
          className={[
            "flex h-12 w-12 shrink-0 items-center justify-center rounded-full ring-2 font-display text-lg font-bold",
            tier.ring,
            badge.earned ? tier.medal : "bg-muted text-muted-foreground grayscale",
          ].join(" ")}
          aria-label={
            badge.earned ? `${badge.title} earned` : `${badge.title} locked`
          }
        >
          {badge.earned ? (badge.tier[0] ?? "?").toUpperCase() : "🔒"}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="truncate text-sm font-semibold text-foreground">
              {badge.title}
            </h3>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {badge.tier}
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{badge.description}</p>
          <p
            className={[
              "mt-2 text-xs font-medium",
              badge.earned ? "text-foreground" : "text-muted-foreground",
            ].join(" ")}
          >
            {awardedLine}
          </p>
        </div>
      </div>
    </li>
  );
}
